/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { useQueries } from 'react-query';
import { Alert, Divider } from '@nextiva/next-ui-components';
import PropTypes from 'prop-types';
import { Page } from 'containers';
import UserRoleGrid from 'components/UserRolesGrid';
import ActionHeader from 'components/ActionHeader';
import { CREATE_ROLE, CLONE_ROLE } from 'constants/drawerTitles';
import { getRoles } from 'api/roleApi';
import { getServiceResources } from 'api/crmApi';
import { getPermissionsMacros } from 'api/permissionsApi';
import { useSnackbar } from 'hooks';
import { toggleState } from 'utils/state-helpers';
import { findLicenseFeature } from 'utils/license-helpers';

import RoleDrawer from './RoleDrawer';
import DeleteRoleModal from './DeleteRoleModal';
import { ShadowUserRoleGrid } from './ShadowDom/index';
import { filterCustomPermissions } from './helpers';
import { getPermissionsHelper } from './utils/crud';

const buttonStyle = {
  width: '136px',
  height: '40px',
  padding: '0px',
};

/**
 * Roles and Permissions data flow
 * Roles data is fetched on Roles mount and puts the rolesList
 * RolesContext. Then user then clicks either a role card or the
 * create button which opens The RoleDrawer. The RoleDrawer fetches
 * the role data and populates the role permissions form.
 * Role templates are declared in `constants/templates.js`
 *
 * The RoleDrawer/index.js component fetches the permissions data and sets it in RoleContext.
 * The RoleForm/index.js component gets the role name from RoleContext.
 * The RoleTabs.js component gets the permissions from RoleContext and builds the tabs.
 * The Form.js component get permissions from RoleTabs.js as a prop[macro] it also has the parent permission click handlers
 */

const Roles = ({
  deleteModalOpen,
  licenses,
  isLoading,
  tabsMacros,
  customPermissions,
  toggleDeleteModal,
  toggleDrawer,
  drawerOpen,
  setDrawerTitle,
  setRole,
  setRoleList,
  setIsLoading,
  setTabMacros,
  setCustomPermissions,
}) => {
  const crmLicense = findLicenseFeature(licenses.data, 'CRM_BASE_FEATURE');

  const [showNotification, setShowNotification] = useState(true);

  const setSnackbar = useSnackbar();

  const [
    { isLoading: rolesLoading, data: rolesData, isError: rolesError },
    { isLoading: macrosLoading, data: macrosData, isError: macrosError },
    {
      isLoading: resourcesLoading,
      data: resourcesData,
      isError: resourcesError,
    },
  ] = useQueries([
    { queryKey: 'getRoles', queryFn: () => getRoles() },
    {
      queryKey: 'getPermissionsMacros',
      queryFn: getPermissionsMacros,
      enabled: tabsMacros.length === 0,
    },
    {
      queryKey: 'getServiceResources',
      queryFn: getServiceResources,
      enabled: !!(customPermissions.length === 0 && crmLicense),
    },
  ]);

  const onClickRoleCardHandler = (role, dt) => {
    let drawerTitle = dt;

    const name = role?.name;

    if (drawerTitle !== CLONE_ROLE && !role?.user_defined) {
      drawerTitle = name;
    }

    toggleDrawer();
    setDrawerTitle(drawerTitle);

    if (drawerTitle !== CREATE_ROLE) {
      setRole(role);
    }
  };

  const getRolesList = (payload) => {
    if (rolesError) {
      setSnackbar({
        message: 'An error occurred while retrieving roles.',
        severity: 'error',
      });
    }

    setRoleList(payload.result);
    setIsLoading(false);
  };

  const getPermissionsMacrosTabs = (payload) => {
    if (macrosError) {
      setSnackbar({
        message: 'An error occurred while retrieving permissions macros.',
        severity: 'error',
      });
    }
    /**
     * Add parent property to roles
     */
    // Defensive check
    if (!payload || !Array.isArray(payload.tabs)) {
      setTabMacros([]);
      return;
    }
    const macros = getPermissionsHelper(payload);

    setTabMacros(macros);
  };

  const getCustomPermissions = (payload) => {
    if (resourcesError) {
      setSnackbar({
        message: 'An error occurred while retrieving custom permissions.',
        severity: 'error',
      });
    }

    const permissions = filterCustomPermissions(payload?.data ?? []);

    setCustomPermissions(permissions);
  };

  const setSnackbarError = () => {
    setSnackbar({
      autoHideDuration: 5000,
      severity: 'error',
      message:
        'An error occurred while deleting the role, please try again later.',
    });
  };

  const setSnackbarSuccess = () => {
    setSnackbar({
      autoHideDuration: 5000,
      severity: 'success',
      message: 'Role deleted successfully!',
    });
  };

  useEffect(() => {
    if (rolesLoading || macrosLoading || resourcesLoading) {
      return;
    }
    getRolesList(rolesData);
    if (tabsMacros.length === 0) getPermissionsMacrosTabs(macrosData);
    if (customPermissions.length === 0 && crmLicense)
      getCustomPermissions(resourcesData);
  }, [rolesLoading, rolesData, macrosLoading, resourcesLoading]);

  return (
    <Page className="m-roles">
      {showNotification && (
        <Alert
          fullWidth
          onClose={toggleState(setShowNotification)}
          severity="info"
        >
          <div>
            Roles allow you to group permissions together and assign them across
            your users. Use them to restrict access to who can perform admin
            functions, see cases, edit opportunities and much more.
            <br />
            <u>
              <a
                href="https://www.nextiva.com/support/articles/managing-users-roles-and-permissions-in-nextiva-service-crm.html"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="PlatformAdminUI-RolesAndPermissions-Roles-AlertMessage"
              >
                Learn: Best practices for roles and permissions
              </a>
            </u>
          </div>
        </Alert>
      )}
      <div className={showNotification ? 'm-roles__header' : ''}>
        <ActionHeader
          label="User Roles"
          actionName="Create role"
          buttonStyle={buttonStyle}
          onClick={onClickRoleCardHandler}
          data-testid="PlatformAdminUI-RolesAndPermissions-Roles-Header"
        />
      </div>
      <Divider />
      {drawerOpen && <RoleDrawer />}
      {isLoading && <ShadowUserRoleGrid />}
      {!isLoading && (
        <UserRoleGrid onClickRoleCardHandler={onClickRoleCardHandler} />
      )}
      {deleteModalOpen && (
        <DeleteRoleModal
          toggleDeleteModal={toggleDeleteModal}
          setSnackbarError={setSnackbarError}
          setSnackbarSuccess={setSnackbarSuccess}
        />
      )}
    </Page>
  );
};

Roles.propTypes = {
  customPermissions: PropTypes.array,
  deleteModalOpen: PropTypes.bool,
  drawerOpen: PropTypes.bool,
  isLoading: PropTypes.bool,
  licenses: PropTypes.object,
  setCustomPermissions: PropTypes.func,
  setDrawerTitle: PropTypes.func,
  setIsLoading: PropTypes.func,
  setRole: PropTypes.func,
  setRoleList: PropTypes.func,
  setTabMacros: PropTypes.func,
  tabsMacros: PropTypes.array,
  toggleDeleteModal: PropTypes.func,
  toggleDrawer: PropTypes.func,
};

export default Roles;
